import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Alert, Descriptions, Tag, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import Loader from '@/components/Loader';
import { NAMESPACE } from '@/constants';
import { useDownloadDocumentMutation } from '@/api';
import type { IoDocument } from '@/api';
import { ioFileName } from '@/utils';
import dayjs from 'dayjs';
import { LoadingState, LoadingHint, PreviewFrame, FactsBlock } from './IoPreviewDrawer.styles';

interface Props {
  document: IoDocument | null;
  onClose: () => void;
}

export const IoPreviewDrawer: FC<Props> = ({ document, onClose }) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const [downloadDocument] = useDownloadDocumentMutation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Loading covers both the blob download AND the iframe painting the PDF, so the
  // spinner only clears once the document is actually visible.
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [isError, setIsError] = useState(false);
  // The backend polls PandaDoc for up to ~12s while the PDF generates, so after a
  // short delay we explain the wait rather than leaving a bare spinner on screen.
  const [isSlow, setIsSlow] = useState(false);

  const documentId = document?.documentId;

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const load = async () => {
      if (!documentId) return;
      setIsDownloading(true);
      setIsRendering(true);
      setIsError(false);
      setIsSlow(false);
      try {
        const pdf = await downloadDocument(documentId).unwrap();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(pdf);
        setPreviewUrl(objectUrl);
      } catch {
        if (!cancelled) {
          setIsError(true);
          setIsRendering(false);
        }
      } finally {
        if (!cancelled) setIsDownloading(false);
      }
    };

    load();

    // After 3s the wait is almost certainly PandaDoc still rendering the PDF; say so.
    const slowTimer = setTimeout(() => {
      if (!cancelled) setIsSlow(true);
    }, 3000);

    return () => {
      cancelled = true;
      clearTimeout(slowTimer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPreviewUrl(null);
      setIsDownloading(false);
      setIsRendering(false);
      setIsError(false);
      setIsSlow(false);
    };
    // Keyed on the document id so switching documents reloads, but a stable
    // `downloadDocument` identity change (or StrictMode remount) doesn't leave
    // the spinner stuck.
  }, [documentId, downloadDocument]);

  const isLoading = isDownloading || (isRendering && !isError);

  return (
    <Drawer
      title={document?.messageTitle}
      width="60%"
      placement="right"
      open={Boolean(document)}
      onClose={onClose}
      destroyOnHidden
      extra={
        /* The viewer's own download button names the file after the blob URL
           -- a random id -- and a blob carries no Content-Disposition to say
           otherwise. Saving through a link is the only way to name it. */
        document && (
          <Button
            icon={<DownloadOutlined />}
            href={previewUrl ?? undefined}
            download={ioFileName(document)}
            disabled={!previewUrl}
          >
            {t('ioDownload')}
          </Button>
        )
      }
    >
      {/* What the table cannot carry: who the document is for, and whether a
          later revision has taken over from it. Budget and flight live in the
          table instead, where they can be scanned and sorted across documents
          rather than found one at a time. */}
      <FactsBlock>
        <Descriptions size="small" column={2} colon={false}>
          {/* No version row: the revision is already in the document's own
              title, which the drawer header and the Name column both show, so
              stating it again earns nothing. Whether a revision has been
              replaced is not in the title, so that stays — as a tag beside the
              campaign rather than a row of its own. */}
          {document?.advertiserName && (
            <Descriptions.Item label={t('ioAdvertiser')}>{document.advertiserName}</Descriptions.Item>
          )}
          {document?.campaignName && (
            <Descriptions.Item label={t('ioCampaign')} span={2}>
              {document.campaignName}
              {document.supersededAt && (
                <Tag className="status-superseded" style={{ marginLeft: 8 }}>
                  {t('ioStatusSuperseded')}
                </Tag>
              )}
            </Descriptions.Item>
          )}
          {document?.supersededAt && (
            <Descriptions.Item label={t('ioSupersededOn')} span={2}>
              {dayjs(document.supersededAt).format('DD MMM YYYY, HH:mm')}
            </Descriptions.Item>
          )}
        </Descriptions>
      </FactsBlock>

      {isError ? (
        <Alert type="error" role="alert" message={t('ioPreviewFailed')} showIcon />
      ) : (
        <>
          {isLoading && (
            <LoadingState direction="vertical" align="center" size="small" role="status" aria-live="polite" aria-busy>
              <Loader fullscreen={false} />
              <span>{t('ioPreviewLoading')}</span>
              {isSlow && <LoadingHint>{t('ioPreviewGenerating')}</LoadingHint>}
            </LoadingState>
          )}
          {previewUrl && (
            <PreviewFrame
              title={document?.messageTitle}
              src={previewUrl}
              onLoad={() => setIsRendering(false)}
              $hidden={isLoading}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default IoPreviewDrawer;
